import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Save, Users, Clock, Copy, Pencil, Phone, Mail, MessageCircle, KeyRound, Package, Layers, X, Award, ImageIcon, Upload } from "lucide-react";
import { defaultSchedule, type WeekSchedule, type TimeRange } from "@/hooks/use-working-hours";
import {
  STAFF_PERMISSIONS,
  STAFF_ACCESS_STATUS,
  permissionsFromRole,

} from "@/lib/staff-permissions";
import { useAccessRoles } from "@/hooks/use-access-roles";
import AccessRolesManager from "@/components/admin/AccessRolesManager";

interface StaffRow {
  id: string;
  name: string;
  email: string;
  phone: string;
  whatsapp: string;
  specialties: string;
  notes: string;
  user_id: string | null;
  is_active: boolean;
  access_role: string;
  access_status: string;
  permissions: Record<string, boolean>;
  working_hours: WeekSchedule;
  photo_url: string;
  bio: string;
  certificates: Certificate[];
}

export interface Certificate {
  title: string;
  description: string;
  image_url: string;
}

const emptyCertificate: Certificate = { title: "", description: "", image_url: "" };

const emptyStaff: Omit<StaffRow, "id" | "working_hours"> = {
  name: "",
  email: "",
  phone: "",
  whatsapp: "",
  specialties: "",
  notes: "",
  user_id: null,
  is_active: true,
  access_role: "colaborador",
  access_status: "pending",
  permissions: permissionsFromRole("colaborador"),
  photo_url: "",
  bio: "",
  certificates: [],
};


const dayNames = [
  { key: "0", label: "Domingo" },
  { key: "1", label: "Segunda-Feira" },
  { key: "2", label: "Terça-Feira" },
  { key: "3", label: "Quarta-Feira" },
  { key: "4", label: "Quinta-Feira" },
  { key: "5", label: "Sexta-Feira" },
  { key: "6", label: "Sábado" },
];

const StaffAdmin = () => {
  const [staffList, setStaffList] = useState<StaffRow[]>([]);
  const [editingSchedule, setEditingSchedule] = useState<StaffRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(emptyStaff);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [allServices, setAllServices] = useState<{ id: string; name: string }[]>([]);
  const [servicesDialogStaff, setServicesDialogStaff] = useState<StaffRow | null>(null);
  const [assignedServiceIds, setAssignedServiceIds] = useState<string[]>([]);
  const [savingServices, setSavingServices] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [editingCategory, setEditingCategory] = useState<{ index: number; value: string } | null>(null);
  const { toast } = useToast();
  const { roles: accessRoles, saveRoles: saveAccessRoles, getRole } = useAccessRoles();

  const fetchCategories = async () => {
    const { data } = await supabase.from("app_settings").select("value").eq("key", "service_categories").single();
    if (data?.value && Array.isArray(data.value)) {
      setCategories(data.value as string[]);
    } else {
      // Fallback defaults
      setCategories(["Massagista", "Cabeleireira", "Manicure", "Sobrancelha", "Maquiagem", "Estética", "Podologia"]);
    }
  };

  const saveCategories = async (updated: string[]) => {
    setCategories(updated);
    await supabase.from("app_settings").upsert({ key: "service_categories", value: updated as any }, { onConflict: "key" });
  };

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name || categories.includes(name)) return;
    await saveCategories([...categories, name]);
    setNewCategory("");
    toast({ title: "Categoria adicionada" });
  };

  const deleteCategory = async (cat: string) => {
    await saveCategories(categories.filter(c => c !== cat));
    toast({ title: "Categoria removida" });
  };

  const updateCategory = async (index: number, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const oldName = categories[index];
    const updated = [...categories];
    updated[index] = trimmed;
    await saveCategories(updated);
    // Update staff that had the old category name
    if (oldName !== trimmed) {
      for (const s of staffList) {
        if (s.specialties.split(",").map(c => c.trim()).includes(oldName)) {
          const newSpecs = s.specialties.split(",").map(c => c.trim() === oldName ? trimmed : c.trim()).join(", ");
          await supabase.from("staff").update({ specialties: newSpecs }).eq("id", s.id);
        }
      }
      fetchStaff();
    }
    setEditingCategory(null);
    toast({ title: "Categoria atualizada" });
  };

  const fetchStaff = async () => {
    const { data } = await supabase.from("staff").select("*").order("name");
    setStaffList(
      (data || []).map((s: any) => ({
        ...s,
        email: s.email || "",
        phone: s.phone || "",
        whatsapp: s.whatsapp || "",
        specialties: s.specialties || "",
        notes: s.notes || "",
        photo_url: s.photo_url || "",
        bio: s.bio || "",
        certificates: Array.isArray(s.certificates) ? s.certificates : [],
        working_hours: typeof s.working_hours === "string"
          ? JSON.parse(s.working_hours)
          : s.working_hours || defaultSchedule,
      }))

    );
  };

  const uploadImage = async (file: File, prefix: string): Promise<string | null> => {
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const path = `${prefix}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("cms-images").upload(path, file, { upsert: true });
    if (error) {
      toast({ title: "Erro no upload", description: error.message, variant: "destructive" });
      return null;
    }
    const { data } = supabase.storage.from("cms-images").getPublicUrl(path);
    toast({ title: "Imagem carregada!" });
    return data.publicUrl;
  };


  useEffect(() => {
    fetchStaff();
    fetchCategories();
    supabase.from("services").select("id, name").eq("is_active", true).order("name").then(({ data }) => {
      setAllServices((data as { id: string; name: string }[]) || []);
    });
  }, []);

  const openServicesDialog = async (staff: StaffRow) => {
    setServicesDialogStaff(staff);
    const { data } = await supabase.from("staff_services").select("service_id").eq("staff_id", staff.id);
    setAssignedServiceIds((data || []).map((r: any) => r.service_id));
  };

  const toggleServiceAssignment = (serviceId: string) => {
    setAssignedServiceIds((prev) =>
      prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
    );
  };

  const saveServiceAssignments = async () => {
    if (!servicesDialogStaff) return;
    setSavingServices(true);
    // Delete existing and re-insert
    await supabase.from("staff_services").delete().eq("staff_id", servicesDialogStaff.id);
    if (assignedServiceIds.length > 0) {
      await supabase.from("staff_services").insert(
        assignedServiceIds.map((sid) => ({ staff_id: servicesDialogStaff.id, service_id: sid }))
      );
    }
    toast({ title: "Serviços do colaborador atualizados!" });
    setSavingServices(false);
    setServicesDialogStaff(null);
  };

  const openNewForm = () => {
    setFormData(emptyStaff);
    setEditingId(null);
    setFormOpen(true);
  };

  const openEditForm = (staff: StaffRow) => {
    setFormData({
      name: staff.name,
      email: staff.email,
      phone: staff.phone,
      whatsapp: staff.whatsapp,
      specialties: staff.specialties,
      notes: staff.notes,
      user_id: staff.user_id,
      is_active: staff.is_active,
      access_role: staff.access_role || "colaborador",
      access_status: staff.access_status || "pending",
      permissions: (staff.permissions as Record<string, boolean>) || getRole(staff.access_role || "colaborador")?.permissions || permissionsFromRole("colaborador"),
      photo_url: staff.photo_url || "",
      bio: staff.bio || "",
      certificates: staff.certificates || [],
    });
    setEditingId(staff.id);
    setFormOpen(true);
  };


  const saveStaff = async () => {
    if (!formData.name.trim()) {
      toast({ title: "Nome é obrigatório", variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      whatsapp: formData.whatsapp.trim(),
      specialties: formData.specialties.trim(),
      notes: formData.notes.trim(),
      is_active: formData.is_active,
      access_role: formData.access_role,
      access_status: formData.access_status,
      permissions: formData.permissions as any,
      photo_url: formData.photo_url.trim(),
      bio: formData.bio.trim(),
      certificates: (formData.certificates || []).filter(c => c.title.trim() || c.image_url) as any,
    };


    const { data: saved, error } = editingId
      ? await supabase.from("staff").update(payload as any).eq("id", editingId).select("id, user_id").maybeSingle()
      : await supabase.from("staff").insert(payload as any).select("id, user_id").maybeSingle();

    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      // Categoria "Administrador" => acesso total ao painel admin
      if (saved?.id && saved?.user_id) {
        const grant = formData.access_role === "admin" && formData.access_status === "approved" && formData.is_active;
        const { data: res } = await supabase.functions.invoke("set-staff-admin-role", {
          body: { staff_id: saved.id, grant },
        });
        if (res?.error) {
          toast({ title: "Aviso sobre acesso admin", description: res.error });
        } else if (grant) {
          toast({ title: "Acesso de administrador concedido" });
        }
      } else if (formData.access_role === "admin") {
        toast({
          title: "Crie a conta de acesso",
          description: "Para o acesso total ao painel, crie primeiro a conta de login deste colaborador.",
        });
      }
      toast({ title: editingId ? "Colaborador atualizado!" : "Colaborador adicionado!" });
      setFormOpen(false);
      fetchStaff();
    }
    setSaving(false);
  };


  const createStaffAccount = async (staffId: string) => {
    if (!accountEmail || !accountPassword) {
      toast({ title: "Preencha email e senha para a conta", variant: "destructive" });
      return;
    }
    if (accountPassword.length < 6) {
      toast({ title: "A senha deve ter pelo menos 6 caracteres", variant: "destructive" });
      return;
    }
    setCreatingAccount(true);
    const { data, error } = await supabase.functions.invoke("create-staff-account", {
      body: { email: accountEmail, password: accountPassword, staff_id: staffId },
    });
    if (error || data?.error) {
      toast({ title: "Erro", description: data?.error || error?.message, variant: "destructive" });
    } else {
      toast({
        title: "Conta criada, aguarda aprovação",
        description: "O acesso fica pendente até ser aprovado por si nesta ficha.",
      });
      setAccountEmail("");
      setAccountPassword("");
      fetchStaff();
    }
    setCreatingAccount(false);
  };

  const setAccessStatus = async (status: string) => {
    if (!editingId) return;
    const { data: sess } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("staff")
      .update({
        access_status: status,
        approved_at: status === "approved" ? new Date().toISOString() : null,
        approved_by: status === "approved" ? sess?.user?.id || null : null,
      })
      .eq("id", editingId);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
      return;
    }
    setFormData((f) => ({ ...f, access_status: status }));
    toast({
      title:
        status === "approved"
          ? "Acesso aprovado"
          : status === "suspended"
          ? "Acesso suspenso"
          : "Acesso colocado como pendente",
    });
    fetchStaff();
  };

  const toggleActive = async (id: string, active: boolean) => {
    await supabase.from("staff").update({ is_active: active }).eq("id", id);
    fetchStaff();
  };

  const deleteStaff = async (id: string) => {
    await supabase.from("staff").delete().eq("id", id);
    toast({ title: "Colaborador removido!" });
    fetchStaff();
  };

  // Schedule editor logic
  const openScheduleEditor = (staff: StaffRow) => {
    setEditingSchedule({ ...staff, working_hours: JSON.parse(JSON.stringify(staff.working_hours)) });
  };

  const updateDay = (dayKey: string, updates: Partial<WeekSchedule[string]>) => {
    if (!editingSchedule) return;
    setEditingSchedule({
      ...editingSchedule,
      working_hours: {
        ...editingSchedule.working_hours,
        [dayKey]: { ...editingSchedule.working_hours[dayKey], ...updates },
      },
    });
  };

  const addRange = (dayKey: string) => {
    if (!editingSchedule) return;
    const day = editingSchedule.working_hours[dayKey];
    const lastRange = day.ranges[day.ranges.length - 1];
    const newStart = lastRange ? lastRange.end : "14:00";
    updateDay(dayKey, { ranges: [...day.ranges, { start: newStart, end: "18:00" }] });
  };

  const removeRange = (dayKey: string, index: number) => {
    if (!editingSchedule) return;
    const day = editingSchedule.working_hours[dayKey];
    if (day.ranges.length <= 1) return;
    updateDay(dayKey, { ranges: day.ranges.filter((_, i) => i !== index) });
  };

  const updateRange = (dayKey: string, index: number, updates: Partial<TimeRange>) => {
    if (!editingSchedule) return;
    const day = editingSchedule.working_hours[dayKey];
    const newRanges = day.ranges.map((r, i) => i === index ? { ...r, ...updates } : r);
    updateDay(dayKey, { ranges: newRanges });
  };

  const copyToAll = (dayKey: string) => {
    if (!editingSchedule) return;
    const source = editingSchedule.working_hours[dayKey];
    const newHours = { ...editingSchedule.working_hours };
    for (let d = 0; d < 7; d++) {
      const k = d.toString();
      if (k !== dayKey) {
        newHours[k] = { ...newHours[k], ranges: JSON.parse(JSON.stringify(source.ranges)) };
      }
    }
    setEditingSchedule({ ...editingSchedule, working_hours: newHours });
    toast({ title: "Horário copiado para todos os dias!" });
  };

  const saveSchedule = async () => {
    if (!editingSchedule) return;
    setSaving(true);
    const { error } = await supabase.from("staff").update({
      working_hours: editingSchedule.working_hours as any,
    }).eq("id", editingSchedule.id);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Horários atualizados!" });
      setEditingSchedule(null);
      fetchStaff();
    }
    setSaving(false);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold mb-1">Colaboradores</h1>
          <p className="text-muted-foreground">
            Gerir colaboradores, contactos e horários de trabalho.
          </p>
        </div>
        <Button onClick={openNewForm} className="gap-2">
          <Plus size={16} /> Novo Colaborador
        </Button>
      </div>

      {/* Categories Management */}
      <div className="mb-8 max-w-3xl">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Layers size={18} className="text-primary" />
            <h2 className="font-display text-lg font-semibold">Categorias / Setores</h2>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {categories.map((cat, i) => (
              <div key={cat} className="group flex items-center gap-1 rounded-full border border-border bg-accent/30 px-3 py-1.5 text-sm">
                {editingCategory?.index === i ? (
                  <form onSubmit={(e) => { e.preventDefault(); updateCategory(i, editingCategory.value); }} className="flex items-center gap-1">
                    <Input
                      value={editingCategory.value}
                      onChange={(e) => setEditingCategory({ index: i, value: e.target.value })}
                      className="h-6 w-28 text-xs px-2"
                      autoFocus
                    />
                    <button type="submit" className="text-primary hover:text-primary/80"><Save size={12} /></button>
                    <button type="button" onClick={() => setEditingCategory(null)} className="text-muted-foreground hover:text-foreground"><X size={12} /></button>
                  </form>
                ) : (
                  <>
                    <span className="text-foreground font-medium">{cat}</span>
                    <button onClick={() => setEditingCategory({ index: i, value: cat })} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity"><Pencil size={12} /></button>
                    <button onClick={() => deleteCategory(cat)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"><X size={12} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Nova categoria..."
              className="max-w-[200px] h-9"
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
            />
            <Button size="sm" onClick={addCategory} disabled={!newCategory.trim()} className="gap-1">
              <Plus size={14} /> Adicionar
            </Button>
          </div>
        </div>
      </div>

      {/* Access categories (menus / submenus) */}
      <div className="mb-8 max-w-3xl">
        <AccessRolesManager roles={accessRoles} saveRoles={saveAccessRoles} />
      </div>

      <div className="space-y-3 max-w-3xl">
        {staffList.length === 0 && (
          <div className="text-center py-12 text-muted-foreground border border-dashed border-border rounded-xl">
            <Users size={40} className="mx-auto mb-3 opacity-50" />
            <p>Nenhum colaborador adicionado.</p>
            <p className="text-sm">Clique em "Novo Colaborador" para começar.</p>
          </div>
        )}
        {staffList.map((s) => (
          <div key={s.id} className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <Switch checked={s.is_active} onCheckedChange={(v) => toggleActive(s.id, v)} />
                <div>
                  <span className={`font-semibold text-lg ${s.is_active ? "text-foreground" : "text-muted-foreground line-through"}`}>
                    {s.name}
                  </span>
                  {s.specialties && (
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {s.specialties.split(",").map(c => c.trim()).filter(Boolean).map(cat => (
                        <span key={cat} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{cat}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-muted-foreground">
                    {s.email && (
                      <span className="flex items-center gap-1"><Mail size={12} /> {s.email}</span>
                    )}
                    {s.phone && (
                      <span className="flex items-center gap-1"><Phone size={12} /> {s.phone}</span>
                    )}
                    {s.whatsapp && (
                      <span className="flex items-center gap-1"><MessageCircle size={12} /> {s.whatsapp}</span>
                    )}
                  </div>
                  <div className="flex gap-2 mt-1">
                    <p className="text-xs text-muted-foreground">
                      {Object.values(s.working_hours).filter(d => d.enabled).length} dias ativos
                    </p>
                    {s.user_id ? (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
                          STAFF_ACCESS_STATUS[s.access_status]?.className || "bg-muted text-muted-foreground"
                        }`}
                      >
                        <KeyRound size={10} /> {STAFF_ACCESS_STATUS[s.access_status]?.label || s.access_status}
                      </span>
                    ) : (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Sem conta</span>
                    )}
                    {s.user_id && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {getRole(s.access_role || "colaborador")?.label || s.access_role}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" className="gap-1" onClick={() => openEditForm(s)}>
                  <Pencil size={14} /> Editar
                </Button>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => openServicesDialog(s)}>
                  <Package size={14} /> Serviços
                </Button>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => openScheduleEditor(s)}>
                  <Clock size={14} /> Horários
                </Button>
                <Button size="icon" variant="ghost" onClick={() => deleteStaff(s.id)}>
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Staff Form Dialog (Add/Edit) */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">

          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingId ? "Editar Colaborador" : "Novo Colaborador"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nome *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome completo"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@exemplo.com"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Telefone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+351 900 000 000"
                />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  placeholder="+351 900 000 000"
                />
              </div>
            </div>
            <div>
              <Label>Categorias / Setores</Label>
              <p className="text-xs text-muted-foreground mb-1">Selecione as categorias em que este colaborador atua</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {categories.map((cat) => {
                  const currentCats = formData.specialties ? formData.specialties.split(",").map(c => c.trim()).filter(Boolean) : [];
                  const isSelected = currentCats.includes(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => {
                        const updated = isSelected
                          ? currentCats.filter(c => c !== cat)
                          : [...currentCats, cat];
                        setFormData({ ...formData, specialties: updated.join(", ") });
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                        isSelected
                          ? "border-primary bg-primary/10 text-primary font-medium"
                          : "border-border text-muted-foreground hover:border-primary/30"
                      }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
              {formData.specialties && (
                <p className="text-xs text-muted-foreground mt-2">
                  Selecionadas: <span className="text-foreground">{formData.specialties}</span>
                </p>
              )}
            </div>

            {/* Photo & Bio */}
            <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
              <div className="flex items-center gap-2">
                <ImageIcon size={16} className="text-primary" />
                <Label className="font-semibold">Foto & Biografia</Label>
              </div>
              <div className="flex items-start gap-4">
                <div className="shrink-0">
                  {formData.photo_url ? (
                    <div className="relative">
                      <img src={formData.photo_url} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-primary/20" />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, photo_url: "" })}
                        className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full w-6 h-6 flex items-center justify-center"
                        title="Remover"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-full border-2 border-dashed border-border flex items-center justify-center text-muted-foreground">
                      <Users size={28} />
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const url = await uploadImage(file, "staff-photo");
                      if (url) setFormData((p) => ({ ...p, photo_url: url }));
                      e.target.value = "";
                    }}
                    className="text-xs"
                  />
                  <Input
                    value={formData.photo_url}
                    onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                    placeholder="Ou cole a URL da foto"
                    className="text-xs"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Biografia curta</Label>
                <Textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="Uma breve apresentação do colaborador para a página pública de certificados"
                  rows={3}
                />
              </div>
            </div>

            {/* Certificates */}
            <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Award size={16} className="text-primary" />
                  <Label className="font-semibold">Certificados ({formData.certificates.length}/10)</Label>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  disabled={formData.certificates.length >= 10}
                  onClick={() => setFormData((p) => ({ ...p, certificates: [...p.certificates, { ...emptyCertificate }] }))}
                >
                  <Plus size={14} /> Adicionar
                </Button>
              </div>
              {formData.certificates.length === 0 && (
                <p className="text-xs text-muted-foreground italic">Sem certificados. Pode adicionar até 10.</p>
              )}
              <div className="space-y-3">
                {formData.certificates.map((cert, idx) => (
                  <div key={idx} className="border border-border rounded-lg p-3 bg-background space-y-2 relative">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-primary">#{idx + 1}</span>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => setFormData((p) => ({ ...p, certificates: p.certificates.filter((_, i) => i !== idx) }))}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                    <div className="grid grid-cols-[100px_1fr] gap-3">
                      <div>
                        {cert.image_url ? (
                          <img src={cert.image_url} alt="" className="w-24 h-24 rounded-lg object-cover border border-border" />
                        ) : (
                          <div className="w-24 h-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground">
                            <Upload size={20} />
                          </div>
                        )}
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const url = await uploadImage(file, `staff-cert-${idx}`);
                            if (url) {
                              setFormData((p) => {
                                const arr = [...p.certificates];
                                arr[idx] = { ...arr[idx], image_url: url };
                                return { ...p, certificates: arr };
                              });
                            }
                            e.target.value = "";
                          }}
                          className="text-[10px] mt-1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Input
                          value={cert.title}
                          onChange={(e) => setFormData((p) => {
                            const arr = [...p.certificates];
                            arr[idx] = { ...arr[idx], title: e.target.value };
                            return { ...p, certificates: arr };
                          })}
                          placeholder="Título do certificado"
                          className="text-sm"
                        />
                        <Textarea
                          value={cert.description}
                          onChange={(e) => setFormData((p) => {
                            const arr = [...p.certificates];
                            arr[idx] = { ...arr[idx], description: e.target.value };
                            return { ...p, certificates: arr };
                          })}
                          placeholder="Descrição / entidade emissora / ano"
                          rows={3}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>


            <div>
              <Label>Notas internas</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Observações internas sobre o colaborador"
                rows={3}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
              />
              <Label>Ativo no site (visível para clientes)</Label>
            </div>

            {/* Access role, approval and permissions */}
            <div className="border border-border rounded-xl p-4 space-y-4 bg-muted/30">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <KeyRound size={16} className="text-primary" />
                  <Label className="font-semibold">Acesso e Permissões</Label>
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    STAFF_ACCESS_STATUS[formData.access_status]?.className || "bg-muted text-muted-foreground"
                  }`}
                >
                  {STAFF_ACCESS_STATUS[formData.access_status]?.label || formData.access_status}
                </span>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Categoria de acesso</Label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {accessRoles.map((role) => (
                    <button
                      key={role.key}
                      type="button"
                      onClick={() =>
                        setFormData({ ...formData, access_role: role.key, permissions: { ...role.permissions } })
                      }
                      className={`text-left rounded-xl border p-3 transition-colors ${
                        formData.access_role === role.key
                          ? "border-primary bg-primary/10"
                          : "border-border hover:bg-muted/50"
                      }`}
                    >
                      <p className="text-sm font-medium">{role.label}</p>
                      <p className="text-[11px] text-muted-foreground">{role.description}</p>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  A categoria preenche as permissões abaixo, pode ajustar cada uma individualmente.
                </p>
              </div>

              <div className="space-y-3">
                {Array.from(new Set(STAFF_PERMISSIONS.map((p) => p.group))).map((group) => (
                  <div key={group} className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{group}</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {STAFF_PERMISSIONS.filter((p) => p.group === group).map((perm) => (
                        <label
                          key={perm.key}
                          className="flex items-start gap-3 rounded-xl border border-border bg-background p-3 cursor-pointer hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={!!formData.permissions?.[perm.key]}
                            onCheckedChange={(v) =>
                              setFormData({
                                ...formData,
                                permissions: { ...(formData.permissions || {}), [perm.key]: !!v },
                              })
                            }
                            className="mt-0.5"
                          />
                          <span>
                            <span className="text-sm block">{perm.label}</span>
                            <span className="text-[11px] text-muted-foreground">{perm.hint}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {editingId && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {formData.access_status !== "approved" ? (
                    <Button type="button" size="sm" className="gap-1" onClick={() => setAccessStatus("approved")}>
                      <KeyRound size={14} /> Aprovar acesso
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => setAccessStatus("suspended")}
                    >
                      <X size={14} /> Suspender acesso
                    </Button>
                  )}
                  {formData.access_status !== "pending" && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setAccessStatus("pending")}>
                      Voltar a pendente
                    </Button>
                  )}
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Nenhum acesso é aprovado automaticamente: mesmo depois de criada a conta, o colaborador só entra em
                /colaborador após aprovação do administrador.
              </p>
            </div>

            {/* Account creation - only for existing staff without account */}
            {editingId && !formData.user_id && (
              <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/30">
                <div className="flex items-center gap-2">
                  <KeyRound size={16} className="text-primary" />
                  <Label className="font-semibold">Criar Conta de Acesso</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Crie uma conta para o colaborador aceder ao painel em <strong>/colaborador</strong> e gerir a sua agenda.
                </p>
                <div>
                  <Label className="text-xs">Email de login</Label>
                  <Input
                    type="email"
                    value={accountEmail}
                    onChange={(e) => setAccountEmail(e.target.value)}
                    placeholder="login@exemplo.com"
                  />
                </div>
                <div>
                  <Label className="text-xs">Senha</Label>
                  <Input
                    type="password"
                    value={accountPassword}
                    onChange={(e) => setAccountPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-1"
                  disabled={creatingAccount}
                  onClick={() => createStaffAccount(editingId)}
                >
                  <KeyRound size={14} /> {creatingAccount ? "A criar..." : "Criar Conta"}
                </Button>
              </div>
            )}

            {editingId && formData.user_id && (
              <div className="border border-border rounded-xl p-4 bg-muted/30">
                <div className="flex items-center gap-2 text-sm">
                  <KeyRound size={14} className="text-emerald-600" />
                  <span className="text-emerald-700 font-medium">Conta de acesso vinculada </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  O colaborador pode fazer login em /colaborador para ver a sua agenda e bloquear horários.
                </p>
              </div>
            )}
          </div>
          <Button className="w-full rounded-full gap-1" onClick={saveStaff} disabled={saving}>
            <Save size={16} /> {saving ? "A guardar..." : "Guardar"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Schedule Editor Dialog */}
      <Dialog open={!!editingSchedule} onOpenChange={(open) => { if (!open) setEditingSchedule(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {editingSchedule && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  Horários, {editingSchedule.name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 py-2">
                {dayNames.map(({ key, label }) => {
                  const day = editingSchedule.working_hours[key];
                  return (
                    <div key={key} className={`rounded-lg border border-border p-3 space-y-2 ${!day.enabled ? "opacity-50" : ""}`}>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={day.enabled}
                          onCheckedChange={(v) => updateDay(key, { enabled: v })}
                        />
                        <span className={`text-sm font-medium flex-1 ${!day.enabled ? "line-through text-muted-foreground" : "text-foreground"}`}>
                          {label}
                        </span>
                        {day.enabled && (
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => addRange(key)} title="Adicionar intervalo">
                              <Plus size={14} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyToAll(key)} title="Copiar para todos os dias">
                              <Copy size={14} />
                            </Button>
                          </div>
                        )}
                      </div>
                      {day.enabled && (
                        <div className="space-y-1.5 pl-11">
                          {day.ranges.map((range, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <Input
                                type="time"
                                value={range.start}
                                onChange={(e) => updateRange(key, i, { start: e.target.value })}
                                className="w-28 h-8 text-xs"
                              />
                              <span className="text-muted-foreground text-sm">-</span>
                              <Input
                                type="time"
                                value={range.end}
                                onChange={(e) => updateRange(key, i, { end: e.target.value })}
                                className="w-28 h-8 text-xs"
                              />
                              {day.ranges.length > 1 && (
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeRange(key, i)}>
                                  <Trash2 size={12} />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {!day.enabled && (
                        <p className="text-xs text-muted-foreground italic pl-11">Indisponível</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <Button className="w-full rounded-full gap-1" onClick={saveSchedule} disabled={saving}>
                <Save size={16} /> {saving ? "A guardar..." : "Guardar Horários"}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Services Assignment Dialog */}
      <Dialog open={!!servicesDialogStaff} onOpenChange={(open) => { if (!open) setServicesDialogStaff(null); }}>
        <DialogContent className="max-w-md">
          {servicesDialogStaff && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  Serviços, {servicesDialogStaff.name}
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Selecione os serviços que este colaborador pode realizar. Se nenhum for selecionado, todos os serviços ficam disponíveis.
              </p>
              <div className="space-y-2 max-h-[50vh] overflow-y-auto py-2">
                {allServices.map((svc) => (
                  <label
                    key={svc.id}
                    className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-all ${
                      assignedServiceIds.includes(svc.id) ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                    }`}
                  >
                    <Checkbox
                      checked={assignedServiceIds.includes(svc.id)}
                      onCheckedChange={() => toggleServiceAssignment(svc.id)}
                    />
                    <span className="font-medium text-foreground text-sm">{svc.name}</span>
                  </label>
                ))}
                {allServices.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">Nenhum serviço ativo encontrado.</p>
                )}
              </div>
              <Button className="w-full rounded-full gap-1" onClick={saveServiceAssignments} disabled={savingServices}>
                <Save size={16} /> {savingServices ? "A guardar..." : "Guardar Serviços"}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffAdmin;
